import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sabzy_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err.response?.status === 401) {
      // Don't auto-redirect during login flow; let pages handle
    }
    return Promise.reject(err);
  }
);

export default api;

// ----- Auth -----
export const signup = (data) => api.post("/auth/signup", data);
export const verifyOtp = (data) => api.post("/auth/verify-otp", data);
export const login = (data) => api.post("/auth/login", data);
export const register = (data) => api.post("/auth/register", data);
export const googleSession = (session_id) => api.post("/auth/google/session", { session_id });

// ----- Stripe payments -----
export const createStripeCheckout = (lookup_key) =>
  api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
export const getPaymentStatus = (session_id) => api.get(`/payments/status/${session_id}`);
export const resendOtp = (data) => api.post("/auth/resend-otp", data);
export const fetchMe = () => api.get("/auth/me");
export const requestOtp = (data) => api.post("/auth/request-otp", data);
export const setPassword = (data) => api.post("/auth/set-password", data);
export const forgotPassword = (data) => api.post("/auth/forgot-password", data);

// ----- Plans / Models -----
export const getPlans = () => api.get("/plans");
export const getModels = () => api.get("/models");
export const redeemCode = (code) => api.post("/plans/redeem", { code });

// ----- Chat -----
export const listConversations = () => api.get("/conversations");
export const getConversation = (id) => api.get(`/conversations/${id}`);
export const deleteConversation = (id) => api.delete(`/conversations/${id}`);
export const sendChat = (data) => api.post("/chat", data);
export const shareConversation = (id) => api.post(`/conversations/${id}/share`);
export const unshareConversation = (id) => api.post(`/conversations/${id}/unshare`);
export const getSharedConversation = (token) =>
  axios.get(`${API_BASE}/share/${token}`).then((r) => r.data);

// ----- Admin -----
export const adminLogin = (data) => api.post("/admin/login", data);
export const adminStats = () => api.get("/admin/stats");
export const adminUsers = () => api.get("/admin/users");
export const adminListCodes = () => api.get("/admin/promo-codes");
export const adminCreateCode = (data) => api.post("/admin/promo-codes", data);
export const adminDeleteCode = (id) => api.delete(`/admin/promo-codes/${id}`);
export const adminToggleCode = (id, is_active) =>
  api.patch(`/admin/promo-codes/${id}?is_active=${is_active}`);
export const adminLiveStats = () => api.get("/admin/live-stats");
export const adminListContacts = () => api.get("/admin/contact-messages");
export const adminRevenue = () => api.get("/admin/revenue");

// ----- Admin: developer API keys -----
export const adminListApiKeys = () => api.get("/admin/api-keys");
export const adminCreateApiKey = (data) => api.post("/admin/api-keys", data);
export const adminRevokeApiKey = (id) => api.delete(`/admin/api-keys/${id}`);
export const adminApiKeyUsage = (id) => api.get(`/admin/api-keys/${id}/usage`);

// ----- Voice cloning -----
export const getVoiceConfig = () => api.get("/voice/config");
export const listVoiceProfiles = () => api.get("/voice/profiles");
export const createVoiceProfile = (formData) =>
  api.post("/voice/profiles", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const deleteVoiceProfile = (id) => api.delete(`/voice/profiles/${id}`);
export const generateVoice = (data) => api.post("/voice/generate", data);
export const listVoiceGenerations = () => api.get("/voice/generations");

// ----- Sabzy Agent -----
export const getAgentConfig = () => api.get("/agent/config");
export const runAgent = (prompt, use_web_research = false) =>
  api.post("/agent/run", { prompt, use_web_research });
export const listAgentRuns = () => api.get("/agent/runs");
export const getAgentRun = (id) => api.get(`/agent/runs/${id}`);
export const deleteAgentRun = (id) => api.delete(`/agent/runs/${id}`);
export const downloadAgentZip = (id) => `${api.defaults.baseURL}/agent/runs/${id}/download`;
export const getBillingPlans = () => api.get("/billing/plans");
export const getMySubscription = () => api.get("/billing/me");
export const createPaypalOrder = (plan) => api.post("/billing/order", { plan });
export const capturePaypalOrder = (order_id) => api.post("/billing/order/capture", { order_id });
export const createPaypalSubscription = (plan, cadence = "monthly") =>
  api.post("/billing/subscription", { plan, cadence });
export const switchToYearly = () => api.post("/billing/switch-to-yearly");
export const activatePaypalSubscription = (subscription_id) =>
  api.post("/billing/subscription/activate", { subscription_id });
export const listMySubscriptions = () => api.get("/billing/subscriptions");
export const cancelMySubscription = (sub_id) => api.post(`/billing/subscription/${sub_id}/cancel`);
